import React from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { formatBigNumber } from '../../../SleepHub/sleepHubApi';
import BarList from './GameBars';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_GRID
} from '../../../../component/common/designSystem';
import {
  CHESS_RESULT_RAMP,
  CHESS_MODE_RAMP,
  SEQUENCE_RAMP,
  RANK_RAMP,
  gameColor
} from './gamesTheme';
import {
  useGameDetail,
  orDash,
  asDuration,
  TrendPanel,
  WeekdayPanel,
  GameFootnote,
  BandBar
} from './GameShell';

/**
 * Games → Chess.
 *
 * A row in this collection is a FINISHED GAME, which makes chess the only one
 * of the four with a real outcome to report. Three things are worth an admin's
 * attention:
 *
 *  1. THE MODE TABLE. Challenge mode turns hints and take-backs off; friendly
 *     and daily puzzle leave them on. The gap in win rate between those modes
 *     is the one number here that says something about the game's difficulty
 *     rather than about who happens to be playing it.
 *  2. THE RESIGNATION RATE. Given its own card rather than left inside the
 *     results donut. A rising figure means the bot is too strong for the people
 *     playing — a tuning problem with an obvious fix, and invisible if you only
 *     look at win rate.
 *  3. THE DAILY PUZZLE. One shared puzzle per day, so its solve rate is the
 *     only genuinely like-for-like comparison in the whole section: everybody
 *     attempted the same position.
 *
 * WIN RATE IS A PROPERTY OF THE POPULATION, NOT OF A PERSON. It appears here as
 * a group figure and nowhere near a name.
 */
const ChessPage = ({ fetcher, populationLabel = 'users' }) => {
  const [stats, gate] = useGameDetail(fetcher);
  if (gate) return gate;

  const puzzle = stats?.dailyPuzzle || {};

  const modeWinOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [({ dataPointIndex }) => CHESS_MODE_RAMP[dataPointIndex % CHESS_MODE_RAMP.length]],
    plotOptions: {
      bar: { horizontal: true, borderRadius: 3, barHeight: '45%', distributed: true }
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: CHART_GRID,
    xaxis: {
      categories: (stats?.modeTable || []).map((row) => row.label),
      labels: { formatter: (val) => `${Math.round(val)}%` },
      max: 100
    },
    tooltip: {
      y: {
        formatter: (val, { dataPointIndex }) =>
          `${val}% of ${formatBigNumber(stats?.modeTable?.[dataPointIndex]?.games || 0)} games`
      }
    }
  };

  const winnableModes = (stats?.modeTable || []).filter((row) => row.winRate != null);

  return (
    <div>
      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Playing"
          value={formatBigNumber(stats?.players || 0)}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Games Finished"
          value={formatBigNumber(stats?.plays || 0)}
        />
        <StatCard
          icon={STAT_ICONS.personalBest}
          title="Player Won"
          value={`${stats?.winRate ?? 0}%`}
        />
        {/* Not a failure figure. Somebody stopping a game they were losing is
            ordinary chess — but a rising share means the bot is too strong. */}
        <StatCard icon={STAT_ICONS.more} title="Resigned" value={`${stats?.resignRate ?? 0}%`} />
        <StatCard
          icon={STAT_ICONS.time}
          title="Average Game"
          value={asDuration(stats?.avgDurationSeconds)}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Days Played On"
          value={formatBigNumber(stats?.activeDays || 0)}
        />
      </div>

      {/* ---------- How games end ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="How Games End"
          subtitle="Every finished game"
          data={stats?.resultSplit || []}
          colors={CHESS_RESULT_RAMP}
          unit="games"
          totalLabel="Games"
          legendLimit={5}
        />
        <MiniDonutCard
          title="Which Mode"
          subtitle="Daily puzzle, friendly or challenge"
          data={stats?.modeSplit || []}
          colors={CHESS_MODE_RAMP}
          unit="games"
          totalLabel="Games"
          legendLimit={3}
        />
        <Panel title="Why They Ended" subtitle="The move that finished the game">
          {(stats?.reasonSplit || []).length > 0 ? (
            <BarList
              rows={stats.reasonSplit}
              unit="games"
              colorFor={(index) => RANK_RAMP[index % RANK_RAMP.length]}
            />
          ) : (
            <PanelEmpty>No finished games yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Shape of a game ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <Panel title="How Long a Game Takes" subtitle="Time from first move to last">
          {(stats?.durationSplit || []).some((row) => row.count > 0) ? (
            <BandBar rows={stats.durationSplit} colors={SEQUENCE_RAMP} unit="games" />
          ) : (
            <PanelEmpty>No finished games yet.</PanelEmpty>
          )}
        </Panel>
        <Panel title="How Many Moves" subtitle="Moves played per game">
          {(stats?.moveSplit || []).some((row) => row.count > 0) ? (
            <BandBar rows={stats.moveSplit} colors={SEQUENCE_RAMP} unit="games" />
          ) : (
            <PanelEmpty>No finished games yet.</PanelEmpty>
          )}
        </Panel>
        <Panel title="Win Rate by Mode" subtitle="Challenge mode has no hints or take-backs">
          {winnableModes.length > 0 ? (
            <Chart
              type="bar"
              height={260}
              width="100%"
              series={[
                {
                  name: 'Won',
                  data: (stats?.modeTable || []).map((row) => row.winRate ?? 0)
                }
              ]}
              options={modeWinOptions}
            />
          ) : (
            <PanelEmpty>Not enough games in any one mode to report.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- The daily puzzle ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <Panel
          title="Daily Puzzle"
          subtitle="One shared position a day — the only like-for-like comparison here"
        >
          {puzzle.attempts > 0 ? (
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div>
                <p className="text-[13px] text-gray-500 dark:text-gray-400">Puzzles attempted</p>
                <p className="mt-0.5 text-[22px] font-semibold text-shoorah-btn dark:text-white">
                  {formatBigNumber(puzzle.distinctPuzzles || 0)}
                </p>
              </div>
              <div>
                <p className="text-[13px] text-gray-500 dark:text-gray-400">Attempts</p>
                <p className="mt-0.5 text-[22px] font-semibold text-shoorah-btn dark:text-white">
                  {formatBigNumber(puzzle.attempts || 0)}
                </p>
              </div>
              <div>
                <p className="text-[13px] text-gray-500 dark:text-gray-400">People trying</p>
                <p className="mt-0.5 text-[22px] font-semibold text-shoorah-btn dark:text-white">
                  {formatBigNumber(puzzle.attemptedBy || 0)}
                </p>
              </div>
              <div>
                <p className="text-[13px] text-gray-500 dark:text-gray-400">Solved</p>
                <p className="mt-0.5 text-[22px] font-semibold text-shoorah-btn dark:text-white">
                  {puzzle.solveRate == null ? '-' : `${puzzle.solveRate}%`}
                </p>
              </div>
            </div>
          ) : (
            <PanelEmpty>Nobody has tried the daily puzzle yet.</PanelEmpty>
          )}
        </Panel>
        <MiniDonutCard
          title="How Much People Play"
          subtitle="Games finished per person"
          data={stats?.volumeSplit || []}
          colors={SEQUENCE_RAMP}
          unit="people"
          totalLabel="Players"
          legendLimit={5}
        />
        <MiniDonutCard
          title="Which Side"
          subtitle="Colour the player took"
          data={stats?.colourSplit || []}
          colors={['#9CA3AF', gameColor('chess')]}
          unit="games"
          totalLabel="Games"
          legendLimit={2}
        />
      </div>

      {/* ---------- Mode by mode ---------- */}
      <div className="mt-6">
        <Panel
          title="Mode by Mode"
          subtitle="Where the difficulty sits — a dash means too few games to report"
        >
          {(stats?.modeTable || []).length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Mode</th>
                  <th className="py-2 px-4 font-medium text-center">Games</th>
                  <th className="py-2 px-4 font-medium text-center">Player won</th>
                  <th className="py-2 px-4 font-medium text-center">Average length</th>
                  <th className="py-2 font-medium text-right">Average moves</th>
                </>
              }
            >
              {stats.modeTable.map((row) => (
                <tr key={row.label} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.label}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.games)}</td>
                  <td className="py-2 px-4 text-center">
                    {row.winRate == null ? '-' : `${row.winRate}%`}
                  </td>
                  <td className="py-2 px-4 text-center">{asDuration(row.avgDurationSeconds)}</td>
                  <td className="py-2 text-right">{orDash(row.avgMoves)}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No mode has enough games to report.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Rhythm ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <TrendPanel stats={stats} subtitle="Games finished per day — last 30 days" />
        <WeekdayPanel stats={stats} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Games Each"
          value={stats?.playsPerPlayer ?? 0}
        />
        <StatCard
          icon={STAT_ICONS.rating}
          title="Games Using a Hint"
          value={`${stats?.hintRate ?? 0}%`}
        />
        <StatCard icon={STAT_ICONS.score} title="Average Moves" value={orDash(stats?.avgMoves)} />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title={`Share of ${populationLabel}`}
          value={
            stats?.totalUsers > 0
              ? `${Math.round(((stats.players || 0) / stats.totalUsers) * 1000) / 10}%`
              : '-'
          }
        />
      </div>

      <GameFootnote />
    </div>
  );
};

ChessPage.propTypes = {
  fetcher: PropTypes.func.isRequired,
  populationLabel: PropTypes.string
};

export default ChessPage;
